'use client';

import { useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import { useTabScopedFindingsFilters } from '../../../store/tab/findingsTabScopedFilterStore';
import { useArchivedLifecycleFilter } from '../../../hooks/useArchivedLifecycleFilter';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { resolveCreatedStartBound } from '../../../utils/created-since';
import type { FindingsListPagesOptions } from '../../../hooks/api/useFindingsListPages';
import {
  buildFindingsBulkFilter,
  hasActiveFindingsFilters,
  resolveFindingsColumnSort,
  resolveFindingsListSort,
} from './findings-list.model';

const FINDINGS_SEARCH_DEBOUNCE_MS = 300;

export function useFindingsListFilters({
  endpointId,
  lifecycleStateOverride,
}: {
  endpointId?: string;
  lifecycleStateOverride?: LifecycleStateFilterDto;
}) {
  const store = useTabScopedFindingsFilters();
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const hasPendingSeverityChangeRequestFilter =
    isSeverityChangeRequestEnabled &&
    store.hasPendingSeverityChangeRequestFilter;
  const [debouncedSearchTerm] = useDebounceValue(
    store.searchTerm,
    FINDINGS_SEARCH_DEBOUNCE_MS,
  );
  const lifecycleState = lifecycleStateOverride ?? store.lifecycleState;
  const lifecycle = useArchivedLifecycleFilter({
    lifecycleState,
    lifecycleStateOverride,
  });
  const createdStartBound = resolveCreatedStartBound(store);
  const columnSort = useMemo(
    () => resolveFindingsColumnSort(store.sortModel),
    [store.sortModel],
  );
  const filter = useMemo(
    () =>
      buildFindingsBulkFilter({
        endpointId,
        searchTerm: debouncedSearchTerm,
        severityFilter: store.severityFilter,
        statusFilters: store.statusFilters,
        categoryFilter: store.categoryFilter,
        publishedFilter: store.publishedFilter,
        hasPreventionFilter: store.hasPreventionFilter,
        hasPendingSeverityChangeRequestFilter,
        createdStartDate: createdStartBound,
        createdEndDate: store.createdEndDate,
        lifecycleState: lifecycle.lifecycleQueryParam,
      }),
    [
      debouncedSearchTerm,
      endpointId,
      lifecycle.lifecycleQueryParam,
      createdStartBound,
      hasPendingSeverityChangeRequestFilter,
      store.categoryFilter,
      store.createdEndDate,
      store.hasPreventionFilter,
      store.publishedFilter,
      store.severityFilter,
      store.statusFilters,
    ],
  );
  const effectiveSort = useMemo(
    () =>
      resolveFindingsListSort({
        columnSort,
        groupByField: store.groupByField,
        groupSortOrder: store.groupSortOrder,
      }),
    [columnSort, store.groupByField, store.groupSortOrder],
  );
  const listOptions = useMemo<FindingsListPagesOptions>(
    () => ({ ...filter, ...effectiveSort }),
    [effectiveSort, filter],
  );
  const hasActiveFilters = hasActiveFindingsFilters({
    ...store,
    hasPendingSeverityChangeRequestFilter,
    createdStartDate: createdStartBound,
  });

  return {
    ...store,
    lifecycleState,
    lifecycle,
    columnSort,
    filter,
    effectiveSort,
    listOptions,
    hasActiveFilters,
  };
}

export type FindingsListFilters = ReturnType<typeof useFindingsListFilters>;
